import {
  canonicalReferralCode,
  isReferralCodeFormat,
  REFERRAL_CODE_LIMITS,
  type CreateReferralCodeInput,
  type PanelReferralCodeDto,
  type PanelReferralCodeRedemptionDto,
  type ReferralCodeListStatus,
  type UpdateReferralCodeInput,
} from '@icebreaker/shared';
import type { TranslationKey, Translator } from '@icebreaker/shared/i18n';
import type { TagVariant } from '@icebreaker/ui';
import { calendarDayInstant } from '@/features/sales/model/sales-view';

/**
 * Durum → rozet tonu (P7 `tone` tablosu): Aktif başarı, Pasif nötr, Başlamadı
 * birincil, Süresi doldu çerçeveli, Tavan doldu bekleme.
 */
export const REFERRAL_STATUS_VARIANT: Readonly<Record<ReferralCodeListStatus, TagVariant>> = {
  active: 'success',
  inactive: 'neutral',
  not_started: 'accent',
  expired: 'outline',
  cap_reached: 'warning',
};

/** Takvim günü biçimi: gün + kısa ay (+ yıl). Gün saat dilimi kaymadan öğle anından okunur. */
function dayText(t: Translator, day: string, withYear: boolean): string {
  return t.formatDate(calendarDayInstant(day), {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

/**
 * Geçerlilik sütunu (P7): `1 Eki – 31 Ara 2026`; iki uç aynı yıldaysa yıl
 * yalnız sonda yazılır. Tarihsiz kod "Süresiz"; tek uçlu kod "… itibarıyla" /
 * "… tarihine kadar".
 */
export function validityText(
  t: Translator,
  code: Pick<PanelReferralCodeDto, 'startDay' | 'endDay'>,
): string {
  const { startDay, endDay } = code;
  if (startDay === null && endDay === null) return t.t('admin.referralCodes.validOpen');
  if (startDay !== null && endDay !== null) {
    const sameYear = startDay.slice(0, 4) === endDay.slice(0, 4);
    return t.t('admin.referralCodes.validRange', {
      from: dayText(t, startDay, !sameYear),
      to: dayText(t, endDay, true),
    });
  }
  if (startDay !== null) {
    return t.t('admin.referralCodes.validFrom', { date: dayText(t, startDay, true) });
  }
  return t.t('admin.referralCodes.validUntil', { date: dayText(t, endDay as string, true) });
}

/** Kullanım sütunu (P7): `3 / 50`, tavansız kodda `7 / sınırsız`. */
export function usageText(
  t: Translator,
  code: Pick<PanelReferralCodeDto, 'usageCount' | 'usageCap'>,
): string {
  const used = t.formatNumber(code.usageCount);
  return code.usageCap === null
    ? t.t('admin.referralCodes.usageUnlimited', { used })
    : t.t('admin.referralCodes.usage', { used, cap: t.formatNumber(code.usageCap) });
}

/** Kullanım satırının tarihi (P7 `8 Eki 2026`): gün + kısa ay + yıl. */
export function redemptionDateText(t: Translator, iso: string): string {
  return t.formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Hediyeyi yükleyen personelin görünen adı. Hesap artık yoksa sunucu adı
 * `null` döner ve satır "Silinmiş kullanıcı" yazar (teknik kimlik gelmez,
 * gösterilmez). Monogram da bu metinden türer.
 */
export function redeemerName(
  t: Translator,
  redemption: Pick<PanelReferralCodeRedemptionDto, 'redeemedBy'>,
): string {
  return redemption.redeemedBy.displayName ?? t.t('admin.referralCodes.drawer.removedStaff');
}

/** Tavana kalan kullanım; tavansız kodda `null`. Eksiye düşmez. */
export function remainingUses(
  code: Pick<PanelReferralCodeDto, 'usageCount' | 'usageCap'>,
): number | null {
  return code.usageCap === null ? null : Math.max(0, code.usageCap - code.usageCount);
}

/** Diyaloğun alanları; sayılar metin olarak tutulur (boş alan = değer yok). */
export interface ReferralFormValues {
  readonly code: string;
  readonly gift: string;
  readonly cap: string;
  readonly startDay: string;
  readonly endDay: string;
  readonly note: string;
  readonly active: boolean;
}

export type ReferralFormMode = 'create' | 'edit';

export type ReferralFormField = 'code' | 'gift' | 'cap' | 'endDay';

export type ReferralFormErrors = Partial<Record<ReferralFormField, TranslationKey>>;

export const EMPTY_REFERRAL_FORM: ReferralFormValues = {
  code: '',
  gift: '',
  cap: '',
  startDay: '',
  endDay: '',
  note: '',
  active: true,
};

/** Düzenleme diyaloğu var olan kodun değerleriyle açılır. */
export function formFromCode(code: PanelReferralCodeDto): ReferralFormValues {
  return {
    code: code.name,
    gift: String(code.giftCredits),
    cap: code.usageCap === null ? '' : String(code.usageCap),
    startDay: code.startDay ?? '',
    endDay: code.endDay ?? '',
    note: code.note ?? '',
    active: code.active,
  };
}

const WHOLE_NUMBER = /^\d+$/;

/**
 * İSTEMCİ DOĞRULAMASI (P7 "Doğrulama" hâli). Kurallar sunucuyla AYNI
 * kaynaktan okunur (`REFERRAL_CODE_LIMITS`, `isReferralCodeFormat`); "kod
 * zaten var" yalnız sunucudan gelir. Düzenlemede kod ve hediye kilitli olduğu
 * için denetlenmez.
 */
export function validateReferralForm(
  values: ReferralFormValues,
  mode: ReferralFormMode,
): ReferralFormErrors {
  const errors: ReferralFormErrors = {};
  if (mode === 'create') {
    if (values.code.trim() === '') errors.code = 'admin.referralCodes.dialog.codeRequired';
    else if (!isReferralCodeFormat(values.code))
      errors.code = 'admin.referralCodes.dialog.codeFormat';
    const gift = values.gift.trim();
    if (
      !WHOLE_NUMBER.test(gift) ||
      Number(gift) < REFERRAL_CODE_LIMITS.giftMin ||
      Number(gift) > REFERRAL_CODE_LIMITS.giftMax
    ) {
      errors.gift = 'admin.referralCodes.dialog.giftRange';
    }
  }
  const cap = values.cap.trim();
  if (cap !== '' && (!WHOLE_NUMBER.test(cap) || Number(cap) < 1)) {
    errors.cap = 'admin.referralCodes.dialog.capInvalid';
  }
  if (values.startDay !== '' && values.endDay !== '' && values.endDay < values.startDay) {
    errors.endDay = 'admin.referralCodes.dialog.endBeforeStart';
  }
  return errors;
}

/** "Kaydedilecek: originn100" önizlemesi; biçim hatalıysa ya da alan boşsa yok. */
export function codePreview(raw: string): string | null {
  return raw.trim() !== '' && isReferralCodeFormat(raw) ? canonicalReferralCode(raw) : null;
}

function mutableFields(values: ReferralFormValues): UpdateReferralCodeInput {
  const cap = values.cap.trim();
  const note = values.note.trim();
  return {
    startDay: values.startDay === '' ? null : values.startDay,
    endDay: values.endDay === '' ? null : values.endDay,
    usageCap: cap === '' ? null : Number(cap),
    active: values.active,
    note: note === '' ? null : note,
  };
}

/** OLUŞTURMA GÖVDESİ: ad ve hediye sayısı YALNIZ burada gider. */
export function toCreateBody(values: ReferralFormValues): CreateReferralCodeInput {
  return {
    name: canonicalReferralCode(values.code),
    giftCredits: Number(values.gift.trim()),
    ...mutableFields(values),
  };
}

/**
 * DÜZENLEME GÖVDESİ: ad ve hediye sayısı YOKTUR (değişmez; sunucu bilinmeyen
 * alanı 400 ile reddeder). Değişebilen beş alan her seferinde tam gönderilir;
 * değişmeyen alan için sunucu denetim satırı yazmaz.
 */
export function toUpdateBody(values: ReferralFormValues): UpdateReferralCodeInput {
  return mutableFields(values);
}
